import { format, formatDistanceToNow, isValid, parseISO, type Locale } from 'date-fns';
import { el, de, enUS } from 'date-fns/locale';

const localeMap: Record<string, Locale> = { el, de, en: enUS };

function getLocale(): Locale {
  const lang = localStorage.getItem('wca_language') ?? 'el';
  return localeMap[lang] ?? enUS;
}

let _currencyCode = 'EUR';

export function setSiteCurrency(code: string): void {
  _currencyCode = code;
}

export function formatCurrency(amount: number | string, currency?: string): string {
  const code = currency ?? _currencyCode;
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return new Intl.NumberFormat('en', { style: 'currency', currency: code, minimumFractionDigits: 2 }).format(0);
  return new Intl.NumberFormat('en', { style: 'currency', currency: code, minimumFractionDigits: 2 }).format(num);
}

export function formatDate(dateStr: string | null | undefined, fmt = 'dd/MM/yyyy'): string {
  if (!dateStr) return '-';
  const d = parseISO(dateStr);
  if (!isValid(d)) return '-';
  return format(d, fmt, { locale: getLocale() });
}

export function formatDateTime(dateStr: string | null | undefined): string {
  return formatDate(dateStr, 'dd/MM/yyyy HH:mm');
}

export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = parseISO(dateStr);
  if (!isValid(d)) return '-';
  return formatDistanceToNow(d, { addSuffix: true, locale: getLocale() });
}

export function formatNumber(num: number): string {
  return num.toLocaleString('en');
}

export function formatPercent(value: number, decimals = 1): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#039;': "'",
  '&apos;': "'",
  '&nbsp;': '\u00A0',
  '&rarr;': '\u2192',
  '&larr;': '\u2190',
  '&mdash;': '\u2014',
  '&ndash;': '\u2013',
  '&hellip;': '\u2026',
  '&euro;': '\u20AC',
  '&pound;': '\u00A3',
  '&times;': '\u00D7',
};

export function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, n) => String.fromCharCode(parseInt(n, 16)))
    .replace(/&[a-zA-Z0-9]+;/g, (entity) => HTML_ENTITIES[entity] ?? entity);
}
